import React, { useState, useContext, useEffect } from 'react'
import { Dialog, Typography, IconButton } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Close } from 'genesis-suite/icons'
import { SwalContext } from 'genesis-suite/components'
import { CloudContext } from '../../components/contexts/CloudContext'
import { SecurityFilters } from '.'
import { userService, architectureService } from '../../lib/services'
import { logEvent } from '../../lib/amplitudeClient'

const useStyles = makeStyles(theme => ({
    dialogPaper: {
        display: 'flex',
        flexFlow: 'column nowrap',
        position: 'relative',
        padding: 12,
        overflowY: 'scroll',
        border: theme.border.default,
        borderRadius: theme.border.radius.round,
        maxHeight: '80vh',
        minHeight: '60vh',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
    },
}))

export default function SecurityFiltersModal({ user, onChange, onClose }) {
    const classes = useStyles()
    const [isSubmitting, setSubmitting] = useState(false)
    const [changes, setChanges] = useState(null)
    const [architecture, setArchitecture] = useState(null)
    const { alert, confirm } = useContext(SwalContext)
    const { selectedCloud: context } = useContext(CloudContext)

    useEffect(() => {
        let cancelCall = () => {}
        architectureService
            .getNodesAndLinks(context, cancel => {
                cancelCall = cancel
            })
            .then(data => {
                setArchitecture(data)
            })
            .catch(err => {
                if (err.__CANCEL__) return
                alert(err.Message || err.message, { type: 'error' })
            })
        return () => cancelCall()
    }, [context])

    useEffect(() => {
        //the modal was closed so discard all changes
        if (!user) setChanges(null)
    }, [user])

    if (!user) return null

    const handleCancel = async () => {
        //compare object references -- staged changes have to be kept in sync when saved
        if (!changes || changes == user) {
            onClose()
        } else {
            const response = await confirm('Are you sure you want to leave? All changes will be lost.', {
                type: 'warning',
                confirmButtonProps: { text: 'Close' },
            })
            if (response.dismiss) return
            onClose()
        }
    }

    const handleSecuritySave = updatedUser => {
        logFilterSave(user, updatedUser)
        setSubmitting(true)
        userService
            .saveUser(updatedUser)
            .then(u => {
                const { userInfo } = u
                alert('User saved successfully!', { type: 'success' })
                onChange({ ...userInfo, ...updatedUser })
                setChanges(null)
            })
            .catch(err => alert(err.Message || err.message, { type: 'error' }))
            .finally(() => setSubmitting(false))
    }

    return (
        <Dialog classes={{ paper: classes.dialogPaper }} open={!!user} maxWidth="lg" fullWidth>
            <div className={classes.header}>
                <Typography display="inline" variant="h5">
                    {user && `${user.firstName} ${user.lastName}'s (${user.userId}) Security Settings`}
                </Typography>
                <IconButton classes={{ root: classes.closeButton }} onClick={handleCancel} size="large">
                    <Close />
                </IconButton>
            </div>
            <SecurityFilters
                className={classes.filters}
                architecture={architecture}
                user={user}
                context={context}
                isSubmitting={isSubmitting}
                onChange={setChanges}
                onSave={handleSecuritySave}
            />
        </Dialog>
    )
}

/** create amplitude log event for filters comparing orginal user object (a) with updated (b) */
function logFilterSave(a, b) {
    const poA = Object.keys(a.portalsPermissions || {})
    const nA = Object.keys(a.nodePermissions || {})
    const prA = Object.keys(a.propertyPermissions || {})
    const sA = (a.securityFilters || []).map(s => s.id)
    const poB = Object.keys(b.portalsPermissions || {})
    const nB = Object.keys(b.nodePermissions || {})
    const prB = Object.keys(b.propertyPermissions || {})
    const sB = (b.securityFilters || []).map(s => s.id)

    const notIn = (a, b) => !a.every(i => b.includes(i))

    logEvent('Update User Filters', {
        addPortalFilter: notIn(poB, poA),
        removePortalFilter: notIn(poA, poB),
        addNodeFilter: notIn(nB, nA),
        removeNodeFilter: notIn(nA, nB),
        addPropertyFilter: notIn(prB, prA),
        removePropertyFilter: notIn(prA, prB),
        addSecurityFilter: notIn(sB, sA),
        removeSecurityFilter: notIn(sA, sB),
    })
}
